import React, { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const COLORS = {
  panel: '#1e293b',
  panelAlt: '#273449',
  border: '#334155',
  text: '#e2e8f0',
  textDim: '#94a3b8',
  accent: '#4ecca3',
  accentLight: '#7efcd2',
  hover: '#1e2a4a',
  danger: '#dc2626',
  dangerBg: '#7f1d1d',
  dangerText: '#fecaca',
};

const TOU_TREATMENTS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'weekday', label: 'Weekday' },
];

const currentYear = new Date().getFullYear();
const DEFAULT_YEARS = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

const PublicHolidaysTable = () => {
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState('ZA');
  const [year, setYear] = useState(currentYear);
  const [knownYears, setKnownYears] = useState(new Set(DEFAULT_YEARS));
  const [coverage, setCoverage] = useState({ minYear: null, maxYear: null, total: 0 });
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Inline edit state: {id, field} where field is 'name' | 'notes'
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Delete confirm state: id being confirmed
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Add form state
  const [adding, setAdding] = useState(false);
  const [newHoliday, setNewHoliday] = useState({
    holiday_date: `${currentYear}-01-01`,
    name: '',
    tou_treatment: 'sunday',
    notes: '',
  });

  // Generate-to-horizon state
  const [generating, setGenerating] = useState(false);
  const [horizonYear, setHorizonYear] = useState(currentYear + 10);
  const [generateInfo, setGenerateInfo] = useState(null);

  // Load countries once
  useEffect(() => {
    fetch(`${API}/api/countries`)
      .then((r) => r.json())
      .then((list) => setCountries(list || []))
      .catch(() => {});
  }, []);

  // Refresh the set of known years whenever country changes (and after generate).
  const refreshKnownYears = useCallback(() => {
    fetch(`${API}/api/public-holidays?country=${country}`)
      .then((r) => r.json())
      .then((list) => {
        const items = list || [];
        const dbYears = new Set();
        items.forEach((h) => {
          if (h.holiday_date) {
            dbYears.add(Number(h.holiday_date.slice(0, 4)));
          }
        });
        if (dbYears.size > 0) {
          setCoverage({
            minYear: Math.min(...dbYears),
            maxYear: Math.max(...dbYears),
            total: items.length,
          });
        } else {
          setCoverage({ minYear: null, maxYear: null, total: 0 });
        }
        // Merge DB years with defaults so the selector always offers at least the near-term
        const merged = new Set([...DEFAULT_YEARS, ...dbYears]);
        setKnownYears(merged);
      })
      .catch(() => {});
  }, [country]);

  useEffect(() => {
    refreshKnownYears();
  }, [refreshKnownYears]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API}/api/public-holidays?country=${country}&year=${year}`)
      .then((r) => r.json())
      .then((list) => {
        const sorted = (list || []).slice().sort((a, b) => a.holiday_date.localeCompare(b.holiday_date));
        setHolidays(sorted);
      })
      .catch((e) => setError('Failed to load: ' + e.message))
      .finally(() => setLoading(false));
  }, [country, year]);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = (id, patch) => {
    setError(null);
    fetch(`${API}/api/public-holidays/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(() => load())
      .catch((e) => setError('Update failed: ' + e.message));
  };

  const beginEdit = (id, field, currentValue) => {
    setEditing({ id, field });
    setEditValue(currentValue || '');
  };

  const commitEdit = () => {
    if (!editing) return;
    updateField(editing.id, { [editing.field]: editValue });
    setEditing(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValue('');
  };

  const handleDelete = (id) => {
    if (confirmDelete !== id) {
      setConfirmDelete(id);
      return;
    }
    fetch(`${API}/api/public-holidays/${id}`, { method: 'DELETE' })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
      })
      .then(() => {
        setConfirmDelete(null);
        load();
      })
      .catch((e) => setError('Delete failed: ' + e.message));
  };

  const handleGenerate = () => {
    setError(null);
    setGenerateInfo(null);
    fetch(`${API}/api/public-holidays/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        country_code: country,
        start_year: year,
        end_year: horizonYear,
      }),
    })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) throw new Error(body.error || 'Generate failed');
        setGenerateInfo(`Generated ${body.inserted} new holidays for ${body.start_year}–${body.end_year} (${body.skipped} existing skipped)`);
        setGenerating(false);
        refreshKnownYears();
        load();
      })
      .catch((e) => setError('Generate failed: ' + e.message));
  };

  const handleAdd = () => {
    if (!newHoliday.name || !newHoliday.holiday_date) {
      setError('Date and name required');
      return;
    }
    setError(null);
    fetch(`${API}/api/public-holidays`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        country_code: country,
        holiday_date: newHoliday.holiday_date,
        name: newHoliday.name,
        tou_treatment: newHoliday.tou_treatment,
        notes: newHoliday.notes || null,
      }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(() => {
        setAdding(false);
        setNewHoliday({
          holiday_date: `${year}-01-01`,
          name: '',
          tou_treatment: 'sunday',
          notes: '',
        });
        load();
      })
      .catch((e) => setError('Add failed: ' + e.message));
  };

  const selectStyle = {
    background: COLORS.panel,
    color: COLORS.text,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '4px',
    padding: '6px 10px',
    fontSize: '12px',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: COLORS.textDim,
    marginBottom: '4px',
  };

  const thStyle = {
    textAlign: 'left',
    padding: '8px 12px',
    borderBottom: `1px solid ${COLORS.border}`,
    color: COLORS.textDim,
    fontSize: '11px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const tdStyle = {
    padding: '8px 12px',
    borderBottom: `1px solid ${COLORS.border}`,
    color: COLORS.text,
    fontSize: '12px',
  };

  const inputStyle = {
    background: COLORS.panelAlt,
    color: COLORS.text,
    border: `1px solid ${COLORS.accent}`,
    borderRadius: '3px',
    padding: '4px 6px',
    fontSize: '12px',
    fontFamily: 'inherit',
  };

  const availableYears = Array.from(new Set([...knownYears, year])).sort((a, b) => a - b);

  return (
    <div style={{ color: COLORS.text, fontFamily: "'Work Sans', sans-serif" }}>
      {/* Filter bar */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'flex-end',
        marginBottom: '14px',
      }}>
        <div>
          <label style={labelStyle}>Country</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)} style={selectStyle}>
            {countries.length === 0 && <option value="ZA">ZA</option>}
            {countries.map((c) => (
              <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Year</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={selectStyle}>
            {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <button
          onClick={() => setAdding(true)}
          disabled={adding}
          style={{
            background: COLORS.accent,
            color: '#0f172a',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: adding ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + Add holiday
        </button>
        <button
          onClick={() => setGenerating(!generating)}
          style={{
            background: 'transparent',
            color: COLORS.text,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '4px',
            padding: '8px 16px',
            fontSize: '12px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Generate to horizon…
        </button>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: COLORS.textDim }}>
          {holidays.length} holiday{holidays.length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Coverage summary */}
      <div style={{
        fontSize: '12px',
        color: COLORS.textDim,
        marginBottom: '12px',
        padding: '8px 12px',
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '4px',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        {coverage.total === 0 ? (
          <>No holidays in database for {country}</>
        ) : (
          <>
            Database coverage: <span style={{ color: COLORS.text }}>{coverage.minYear} – {coverage.maxYear}</span>
            {' · '}
            <span style={{ color: COLORS.text }}>{coverage.maxYear - coverage.minYear + 1}</span> year{coverage.maxYear - coverage.minYear + 1 === 1 ? '' : 's'}
            {' · '}
            <span style={{ color: COLORS.text }}>{coverage.total}</span> holidays total
          </>
        )}
      </div>

      {generating && (
        <div style={{
          background: COLORS.hover,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '12px',
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}>
          <div>
            <label style={labelStyle}>Start year</label>
            <div style={{
              ...selectStyle,
              display: 'inline-block',
              color: COLORS.textDim,
              cursor: 'not-allowed',
            }}>
              {year}
            </div>
          </div>
          <div>
            <label style={labelStyle}>End year (horizon)</label>
            <input
              type="number"
              min={year}
              max={year + 100}
              value={horizonYear}
              onChange={(e) => setHorizonYear(Number(e.target.value))}
              style={{ ...inputStyle, width: '100px', border: `1px solid ${COLORS.border}` }}
            />
          </div>
          <button
            onClick={handleGenerate}
            style={{
              background: COLORS.accent,
              color: '#0f172a',
              border: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Generate
          </button>
          <button
            onClick={() => setGenerating(false)}
            style={{
              background: 'transparent',
              color: COLORS.textDim,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '12px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <div style={{ fontSize: '11px', color: COLORS.textDim, flex: 1, minWidth: '200px' }}>
            Fills in holidays from the <b>holidays</b> library. Existing rows are never overwritten.
            Saturday-falling holidays → saturday TOU, others → sunday.
          </div>
        </div>
      )}

      {generateInfo && (
        <div style={{
          padding: '8px 12px',
          background: '#064e3b',
          color: '#a7f3d0',
          borderRadius: '4px',
          marginBottom: '10px',
          fontSize: '12px',
        }}>
          {generateInfo}
        </div>
      )}

      {error && (
        <div style={{
          padding: '8px 12px',
          background: COLORS.dangerBg,
          color: COLORS.dangerText,
          borderRadius: '4px',
          marginBottom: '10px',
          fontSize: '12px',
        }}>
          {error}
        </div>
      )}

      {loading && <div style={{ color: COLORS.textDim, marginBottom: '10px' }}>Loading…</div>}

      {/* Table */}
      <div style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '6px',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '130px' }}>Date</th>
              <th style={thStyle}>Name</th>
              <th style={{ ...thStyle, width: '150px' }}>TOU treatment</th>
              <th style={thStyle}>Notes</th>
              <th style={{ ...thStyle, width: '90px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && holidays.length === 0 && !adding && (
              <tr>
                <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: COLORS.textDim, fontStyle: 'italic' }}>
                  No holidays for {country} in {year}
                </td>
              </tr>
            )}
            {holidays.map((h) => {
              const isEditingName = editing && editing.id === h.id && editing.field === 'name';
              const isEditingNotes = editing && editing.id === h.id && editing.field === 'notes';
              return (
                <tr key={h.id}>
                  <td style={{ ...tdStyle, fontFamily: "'JetBrains Mono', monospace" }}>
                    {h.holiday_date}
                  </td>
                  <td style={tdStyle}>
                    {isEditingName ? (
                      <input
                        type="text"
                        value={editValue}
                        autoFocus
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEdit();
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        style={{ ...inputStyle, width: '100%' }}
                      />
                    ) : (
                      <span
                        onClick={() => beginEdit(h.id, 'name', h.name)}
                        style={{ cursor: 'pointer', padding: '2px 4px', borderRadius: '3px' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.hover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {h.name}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={h.tou_treatment}
                      onChange={(e) => updateField(h.id, { tou_treatment: e.target.value })}
                      style={{ ...selectStyle, padding: '4px 8px', fontSize: '11px' }}
                    >
                      {TOU_TREATMENTS.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, color: COLORS.textDim }}>
                    {isEditingNotes ? (
                      <input
                        type="text"
                        value={editValue}
                        autoFocus
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitEdit();
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        style={{ ...inputStyle, width: '100%' }}
                      />
                    ) : (
                      <span
                        onClick={() => beginEdit(h.id, 'notes', h.notes)}
                        style={{
                          cursor: 'pointer',
                          padding: '2px 4px',
                          borderRadius: '3px',
                          display: 'inline-block',
                          minWidth: '60px',
                          fontStyle: h.notes ? 'normal' : 'italic',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.hover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {h.notes || '(add note)'}
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleDelete(h.id)}
                      onBlur={() => setConfirmDelete(null)}
                      style={{
                        background: confirmDelete === h.id ? COLORS.danger : 'transparent',
                        color: confirmDelete === h.id ? '#ffffff' : COLORS.textDim,
                        border: `1px solid ${confirmDelete === h.id ? COLORS.danger : COLORS.border}`,
                        borderRadius: '3px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {confirmDelete === h.id ? 'Confirm?' : '✕ Delete'}
                    </button>
                  </td>
                </tr>
              );
            })}

            {adding && (
              <tr style={{ background: COLORS.hover }}>
                <td style={tdStyle}>
                  <input
                    type="date"
                    value={newHoliday.holiday_date}
                    onChange={(e) => setNewHoliday({ ...newHoliday, holiday_date: e.target.value })}
                    style={{ ...inputStyle, fontFamily: "'JetBrains Mono', monospace" }}
                  />
                </td>
                <td style={tdStyle}>
                  <input
                    type="text"
                    placeholder="Holiday name"
                    value={newHoliday.name}
                    autoFocus
                    onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                    style={{ ...inputStyle, width: '100%' }}
                  />
                </td>
                <td style={tdStyle}>
                  <select
                    value={newHoliday.tou_treatment}
                    onChange={(e) => setNewHoliday({ ...newHoliday, tou_treatment: e.target.value })}
                    style={{ ...selectStyle, padding: '4px 8px', fontSize: '11px' }}
                  >
                    {TOU_TREATMENTS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </td>
                <td style={tdStyle}>
                  <input
                    type="text"
                    placeholder="Notes (optional)"
                    value={newHoliday.notes}
                    onChange={(e) => setNewHoliday({ ...newHoliday, notes: e.target.value })}
                    style={{ ...inputStyle, width: '100%' }}
                  />
                </td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={handleAdd}
                      style={{
                        background: COLORS.accent,
                        color: '#0f172a',
                        border: 'none',
                        borderRadius: '3px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      ✓
                    </button>
                    <button
                      onClick={() => setAdding(false)}
                      style={{
                        background: 'transparent',
                        color: COLORS.textDim,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '3px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PublicHolidaysTable;
